import React, { useEffect, useState } from "react";
import { Contact } from "../../api/contact";

interface ContactDetailsModalProps {
    contact: Contact | null;
    isOpen: boolean;
    onClose: () => void;
}

const ContactDetailsModal: React.FC<ContactDetailsModalProps> = ({
    contact,
    isOpen,
    onClose,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isOpen) {
            // Reset position when modal opens
            setPosition({ x: 0, y: 0 });
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest("button, a, .btn-close")) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragStart]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleEscKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleEscKey);
        }
        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen || !contact) return null;

    const initial = contact.contact_Company_name?.charAt(0)?.toUpperCase() || "C";

    return (
        <div
            className="modal fade show"
            style={{
                display: "block",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
                animation: "fadeIn 0.2s ease-in-out",
            }}
            onClick={handleBackdropClick}
        >
            <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .contact-details-modal {
          animation: slideIn 0.3s ease-out;
        }
        .contact-details-modal .modal-header {
          cursor: move;
          user-select: none;
          background: white;
          border-bottom: 1px solid #dee2e6;
          padding: 1rem 1.5rem;
        }
        .contact-details-modal .modal-title {
          color: #dc3545;
          font-weight: 600;
        }
        .custom-btn-close {
          background: transparent;
          border: none;
          color: #dc3545;
          font-size: 1.5rem;
          line-height: 1;
          opacity: 1;
          padding: 0;
          width: auto;
          height: auto;
          transition: opacity 0.2s ease;
        }
        .custom-btn-close:hover {
          opacity: 0.75;
        }
        .custom-btn-close i {
          font-size: 1.25rem;
        }
        .contact-details-modal .modal-content {
          border: none;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          will-change: transform;
        }
        .contact-details-modal .modal-dialog {
          transition: transform 0.05s ease-out;
        }
        .contact-details-modal .modal-content {
          border: none;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }
        .contact-details-modal .modal-body {
          padding: 1.5rem;
          max-height: 70vh;
          overflow-y: auto;
        }
        .contact-details-modal .modal-footer {
          border-top: 1px solid #e9ecef;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
        }
        .info-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .info-section h6 {
          color: #495057;
          font-weight: 600;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #667eea;
        }
        .info-row {
          display: flex;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e9ecef;
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-label {
          font-weight: 600;
          color: #6c757d;
          min-width: 180px;
          flex-shrink: 0;
        }
        .info-value {
          color: #212529;
          word-break: break-word;
        }
        .avatar-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin: 0 auto 1rem;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
        .branch-card {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .branch-header {
          font-weight: 600;
          color: #667eea;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #dee2e6;
        }
      `}</style>

            <div
                className="modal-dialog modal-lg modal-dialog-centered contact-details-modal"
                style={{
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    pointerEvents: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header" onMouseDown={handleMouseDown}>
                        <h5 className="modal-title">
                            <i className="ti ti-eye me-2" />
                            Contact Details
                        </h5>
                        <button
                            type="button"
                            className="btn-close custom-btn-close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <i className="ti ti-x" />
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Avatar and Name Section */}
                        <div className="text-center mb-3">
                            <div className="avatar-circle">{initial}</div>
                            <h4 className="mb-1">{contact.contact_Company_name || "N/A"}</h4>
                            <p className="text-muted mb-2">{contact.contact_name || "N/A"}</p>
                            {contact.status && (
                                <span
                                    className={`badge ${contact.status?.toLowerCase() === "active"
                                        ? "bg-success"
                                        : contact.status?.toLowerCase() === "inactive"
                                            ? "bg-danger"
                                            : "bg-warning"
                                        }`}
                                >
                                    {contact.status}
                                </span>
                            )}
                        </div>

                        {/* Basic Contact Information */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-info-circle me-2" />
                                Basic Information
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Company Name:</span>
                                <span className="info-value">
                                    {contact.contact_Company_name || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Contact Person:</span>
                                <span className="info-value">
                                    {contact.contact_name || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Designation:</span>
                                <span className="info-value">
                                    {contact.contact_desigination || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Phone:</span>
                                <span className="info-value">
                                    {contact.contact_phone || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Email:</span>
                                <span className="info-value">
                                    {contact.contact_mail || "N/A"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Website:</span>
                                <span className="info-value">
                                    {contact.website_url || "N/A"}
                                </span>
                            </div>
                        </div>

                        {/* Lead Information */}
                        <div className="info-section">
                            <h6>
                                <i className="bi bi-bullseye me-2" />
                                Lead Information
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Lead Owner:</span>
                                <span className="info-value">
                                    {contact.username || "Unassigned"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Lead Source:</span>
                                <span className="info-value">
                                    {contact.leadsource_name || "N/A"}
                                </span>
                            </div>
                            {contact.meta_page && (
                                <div className="info-row">
                                    <span className="info-label">Meta Page:</span>
                                    <span className="info-value">{contact.meta_page}</span>
                                </div>
                            )}
                        </div>


                        {/* Branch Information */}
                        {contact.branches && contact.branches.length > 0 && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-map-pin me-2" />
                                    Branch Information ({contact.branches.length})
                                </h6>
                                {contact.branches.map((branch, index) => (
                                    <div key={index} className="branch-card">
                                        <div className="branch-header">
                                            <i className="ti ti-building me-2" />
                                            {branch.branch_name || `Branch ${index + 1}`}
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Address:</span>
                                            <span className="info-value">
                                                {branch.branch_address || "N/A"}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">City:</span>
                                            <span className="info-value">
                                                {branch.branch_city || "N/A"}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">State:</span>
                                            <span className="info-value">
                                                {branch.branch_state || "N/A"}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Country:</span>
                                            <span className="info-value">
                                                {branch.branch_country || "N/A"}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Zipcode:</span>
                                            <span className="info-value">
                                                {branch.branch_zipcode || "N/A"}
                                            </span>
                                        </div>
                                        {branch.gst_number && (
                                            <div className="info-row">
                                                <span className="info-label">GST Number:</span>
                                                <span className="info-value">{branch.gst_number}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Remarks */}
                        {contact.contact_description && (
                            <div className="info-section">
                                <h6>
                                    <i className="ti ti-message me-2" />
                                    Remarks
                                </h6>
                                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                    {contact.contact_description}
                                </p>
                            </div>
                        )}

                        {/* Created Date */}
                        {contact.created_at && (
                            <div className="text-center text-muted small mt-3">
                                <i className="ti ti-calendar me-1" />
                                Created on: {new Date(contact.created_at).toLocaleString()}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light px-4"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactDetailsModal;
